import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowDown,
  Link2,
  Info,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import SEOHead from "../components/shared/SEOHead";
import { PageHero, SectionHeading } from "../components/shared/Primitives";
import {
  QUESTIONS,
  TYPE_ORDER,
  TYPE_INFO,
  X_NOTES,
  computeResult,
} from "../data/carerQuizData";

const PRIVACY_NOTE = "Your quiz answers and result stay on this page and are not submitted to JCC.";

/* ── Single question block ─────────────────────────────────────────── */
function QuestionBlock({ question, selected, onSelect, isMissing }) {
  return (
    <fieldset
      id={`question-${question.id}`}
      className={`scroll-mt-28 rounded-3xl border bg-white p-5 md:p-7 transition-colors ${
        isMissing ? "border-amber-300 ring-2 ring-amber-200" : "border-slate-200"
      }`}
      data-testid={`quiz-question-${question.id}`}
    >
      <legend className="font-display font-semibold text-lg md:text-xl leading-snug px-0">
        <span className="text-purple mr-1.5">{question.id}.</span>
        {question.text}
      </legend>
      {isMissing && (
        <p className="mt-2 text-sm font-medium text-amber-700 flex items-center gap-1.5" data-testid={`quiz-missing-${question.id}`}>
          <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.75} /> Please choose an answer for this question.
        </p>
      )}
      <div className="mt-4 space-y-2.5">
        {question.options.map((opt) => {
          const isSelected = selected === opt.letter;
          return (
            <label
              key={opt.letter}
              className={`flex items-start gap-3 rounded-2xl border p-4 cursor-pointer transition-colors w-full ${
                isSelected
                  ? "border-purple bg-purple/5 ring-1 ring-purple/30"
                  : "border-slate-200 bg-white hover:border-purple/40"
              }`}
              data-testid={`quiz-option-${question.id}-${opt.letter}`}
            >
              <input
                type="radio"
                name={`question-${question.id}`}
                checked={isSelected}
                onChange={() => onSelect(question.id, opt.letter)}
                className="mt-1 h-4 w-4 accent-purple shrink-0"
              />
              <span className="text-sm md:text-base text-ink leading-relaxed">
                <span className="font-semibold mr-1.5">{opt.letter}.</span>
                {opt.text}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/* ── Result summary ────────────────────────────────────────────────── */
function formatThemeNames(themes) {
  const names = themes.map((t) => TYPE_INFO[t].name);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function ResultSummary({ result }) {
  const { leadingThemes, allX, xHits, fewStrengthAnswers } = result;

  return (
    <div id="your-result" className="scroll-mt-28 rounded-3xl border border-purple/20 bg-purple/5 p-6 md:p-10" data-testid="quiz-result-summary">
      <span className="pill bg-purple/10 text-purple" data-testid="quiz-result-pill">
        <Sparkles className="w-3.5 h-3.5 mr-1" /> Your personal result
      </span>

      {allX ? (
        <>
          <h2 className="mt-4 font-display font-bold text-2xl md:text-3xl" data-testid="quiz-result-heading">Something to reflect on</h2>
          <p className="mt-3 text-ink-muted leading-relaxed">
            Your answers did not point strongly to one of the four carer types below. That is still useful
            information — see your reflection notes below for the specific points worth considering.
          </p>
        </>
      ) : (
        <>
          <h2 className="mt-4 font-display font-bold text-2xl md:text-3xl" data-testid="quiz-result-heading">
            Your strongest theme{leadingThemes.length > 1 ? "s" : ""}: {formatThemeNames(leadingThemes)}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="quiz-result-leading-types">
            {leadingThemes.map((t) => (
              <a
                key={t}
                href={`#${TYPE_INFO[t].id}`}
                className="btn-purple text-sm"
                data-testid={`quiz-result-link-${TYPE_INFO[t].id}`}
              >
                {TYPE_INFO[t].name} <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.75} />
              </a>
            ))}
          </div>
          {leadingThemes.length > 1 && (
            <p className="mt-3 text-sm text-ink-muted">Your results were tied — read both sections below; they are equally relevant to you.</p>
          )}
          {fewStrengthAnswers && (
            <p className="mt-4 text-sm text-ink-muted italic" data-testid="quiz-result-caution">
              You chose very few of these answers, so treat this apparent strength result cautiously.
            </p>
          )}
        </>
      )}

      {xHits.length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="quiz-result-reflect-banner">
          <div className="flex items-center gap-2 text-amber-900 font-display font-semibold">
            <AlertTriangle className="w-5 h-5 shrink-0" strokeWidth={1.75} /> Something to reflect on
          </div>
          <p className="mt-2 text-sm text-amber-900 leading-relaxed">
            Some of your answers point to habits worth a closer look. A strong result elsewhere does not cancel this out.
          </p>
          <a href="#reflection-notes" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 underline" data-testid="quiz-result-reflect-link">
            Jump to my reflection notes <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.75} />
          </a>
        </div>
      )}

      <p className="mt-6 text-xs text-ink-soft">
        This is an informal reflection exercise, not a validated assessment of competence or suitability.
      </p>
    </div>
  );
}

/* ── One carer-type description section ────────────────────────────── */
function TypeSection(props) {
  const { themeKey, isLeading } = props;
  const t = TYPE_INFO[themeKey];
  return (
    <section id={t.id} className="scroll-mt-28 section py-12 md:py-16 border-t border-slate-100" data-testid={`quiz-type-section-${t.id}`}>
      <div className="container-jcc max-w-3xl">
        {isLeading && (
          <span className="pill bg-purple/10 text-purple mb-3" data-testid={`quiz-type-leaning-${t.id}`}>
            Your leaning
          </span>
        )}
        <h3 className="font-display font-bold text-2xl md:text-3xl">{t.name}</h3>
        <p className="mt-3 text-ink-muted leading-relaxed">{t.lean}</p>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-teal">Your strength</p>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{t.strength}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Watch for</p>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{t.watchFor}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-purple">Try next</p>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{t.tryNext}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Reflection notes section (always present; specific notes personalised) ── */
function ReflectionNotesSection({ result }) {
  const matched = result ? result.xHits.filter((key) => X_NOTES[key]) : [];
  return (
    <section id="reflection-notes" className="scroll-mt-28 section py-12 md:py-16 border-t border-slate-100 bg-surface" data-testid="quiz-reflection-notes-section">
      <div className="container-jcc max-w-3xl">
        <div className="overline text-teal mb-2">Reflection notes</div>
        <h3 className="font-display font-bold text-2xl md:text-3xl">Something to reflect on</h3>
        <p className="mt-3 text-ink-muted leading-relaxed">
          Some answers point to understandable motives — keeping things calm, maintaining a routine, using experience
          or finding support. The concern is what the chosen approach might overlook.
        </p>

        {!result && (
          <p className="mt-4 text-sm text-ink-muted italic" data-testid="quiz-reflection-notes-pending">
            Complete all ten questions above and select &ldquo;See my result&rdquo; to see whether anything here applies to you.
          </p>
        )}

        {result && matched.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="quiz-reflection-notes-matched">
            <p className="text-sm font-medium text-ink">Based on your answers, these notes are worth a closer look:</p>
            {matched.map((key) => (
              <div key={key} className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid={`quiz-xnote-${key}`}>
                <p className="font-semibold text-amber-900">{X_NOTES[key].label}</p>
                <p className="mt-1.5 text-sm text-amber-900 leading-relaxed">{X_NOTES[key].text}</p>
              </div>
            ))}
          </div>
        )}

        {result && matched.length === 0 && (
          <p className="mt-4 text-sm text-teal flex items-center gap-1.5" data-testid="quiz-reflection-notes-none">
            <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={1.75} /> Nothing here applies to you this time. The guidance below still applies to everyone, whatever your result.
          </p>
        )}

        <div className="mt-8 space-y-4 text-ink-muted leading-relaxed text-sm md:text-base">
          <p>
            If several of these notes sound like your usual approach, pause and look honestly at the pattern. Are you
            missing knowledge or training? Are exhaustion, inadequate support or unrealistic working arrangements
            affecting your decisions? Or do you find yourself resisting the person&rsquo;s preferences and the
            accountability that care requires?
          </p>
          <p>
            Those situations need different responses. Training, supervision and better arrangements can help with
            genuine gaps and pressures. If you understand the responsibilities but consistently do not want to accept
            them, it may be time to consider whether care is the right profession for you.
          </p>
          <p>
            The number of these notes that apply to you does not determine the seriousness of a concern. Working
            beyond competence, failing to report a care mistake or exposing private information deserves attention
            even if it was the only note that applied.
          </p>
          <p className="font-medium text-ink">
            Your next step: Choose the relevant issue and identify a concrete change. Seek appropriate supervision,
            training or professional guidance, and address unsafe practice promptly. People receiving care should never
            have to earn our patience by being easy to look after.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
export default function CarerQuiz() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [missingQuestionId, setMissingQuestionId] = useState(null);

  const answeredCount = Object.keys(answers).length;

  const handleSelect = (questionId, letter) => {
    setAnswers((prev) => ({ ...prev, [questionId]: letter }));
    if (result) setResult(null); // clear previous personal result until recalculated
    if (missingQuestionId === questionId) setMissingQuestionId(null);
  };

  const handleSeeResult = () => {
    const firstMissing = QUESTIONS.find((q) => !answers[q.id]);
    if (firstMissing) {
      setMissingQuestionId(firstMissing.id);
      const el = document.getElementById(`question-${firstMissing.id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const computed = computeResult(answers);
    setResult(computed);
    setMissingQuestionId(null);
    setTimeout(() => {
      document.getElementById("your-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleCopyLink = async () => {
    const cleanUrl = `${window.location.origin}/carer-quiz`;
    try {
      await navigator.clipboard.writeText(cleanUrl);
      toast.success("Quiz link copied — it opens a fresh quiz for the next person.");
    } catch {
      toast.error("Could not copy the link. You can copy it from the address bar instead.");
    }
  };

  return (
    <div data-testid="page-carer-quiz">
      <SEOHead
        title="Carers Are Special People. What Kind of Carer Are You?"
        description="A free, ten-question reflection quiz for carers. Explore your strengths, your habits and the parts of your practice worth a closer look — no sign-up, nothing is stored."
        path="/carer-quiz"
      />

      <PageHero
        eyebrow="A JCC Reflection Quiz · Free, no sign-up"
        title="Carers Are Special People. What Kind of Carer Are You?"
        description="Take our ten-question reflection quiz to explore your strengths, your habits and the parts of your practice worth a closer look."
        accent="purple"
      >
        <a href="#quiz-questions" className="btn-purple" data-testid="quiz-start-btn">
          Start the quiz <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
        </a>
        <a href="#carer-type-jump-links" className="btn-outline" data-testid="quiz-skip-to-types-btn">
          See the carer types
        </a>
      </PageHero>

      {/* ── Introductory article ──────────────────────────────────── */}
      <section className="section py-12 md:py-20">
        <div className="container-jcc max-w-3xl">
          <div className="space-y-4 text-ink-muted leading-relaxed text-sm md:text-base">
            <p className="text-lg md:text-xl font-medium text-ink italic">
              &ldquo;Carers are special people.&rdquo;
            </p>
            <p>
              It is something we hear often. And when you have seen someone bring calm to a frightened person, protect
              somebody&rsquo;s dignity or patiently help them do something they thought they could no longer manage, you
              understand why.
            </p>
            <p>There is something precious about being able to make another person feel safe, heard and respected.</p>
            <p>But those words deserve more than a smile and a pat on the back.</p>
            <p>
              Care is skilled, responsible work. Warmth matters. So do the decisions we make, the promises we keep and
              the things we are willing to learn.
            </p>
            <p>
              For me, a &ldquo;true carer&rdquo; is someone who takes that responsibility seriously, including on the
              ordinary days when nobody is watching.
            </p>

            <h2 className="font-display font-semibold text-xl md:text-2xl text-ink pt-4">The care that happens in the small moments</h2>
            <p>Good care can be wonderfully unshowy.</p>
            <p>
              It might be waiting while somebody chooses what to wear. Remembering that they like their tea in a
              particular cup. Explaining what you are about to do. Listening properly when they say something feels
              wrong.
            </p>
            <p>It might also be keeping a clear record, giving a useful handover or admitting that you need guidance.</p>
            <p>There is care in the kindness. There is care in the organisation. There is care in the courage to speak up.</p>
            <p>
              And there is care in recognising that you are a guest in somebody&rsquo;s home, with a role to fulfil and a
              person&rsquo;s life to respect.
            </p>
            <p>
              A quiet carer can be excellent. A chatty carer can be excellent. Someone new to the profession can bring
              valuable qualities while developing their skills.
            </p>
            <p>We do not all need the same personality. We do need to take the work seriously.</p>

            <h2 className="font-display font-semibold text-xl md:text-2xl text-ink pt-4">Special people still need proper support</h2>
            <p>Let us be clear about something else.</p>
            <p>
              You can care deeply and expect fair pay. You can be committed and need your break. You can build a warm
              relationship and maintain professional boundaries.
            </p>
            <p>Feeling tired, needing help or recognising that a placement is unsuitable does not erase your compassion.</p>
            <p>
              Good intentions need training, practical competence and working arrangements that allow good care to
              happen. Employers, providers and those arranging care have responsibilities too.
            </p>
            <p>No one should have to exhaust themselves to prove they belong in this profession.</p>

            <h2 className="font-display font-semibold text-xl md:text-2xl text-ink pt-4">Before the quiz, check the foundations</h2>
            <p>Whatever your personality, these commitments matter:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Respect the person&rsquo;s choices, privacy and dignity.</li>
              <li>Honour your commitments and communicate promptly when circumstances change.</li>
              <li>Work within your competence and seek guidance when you are unsure.</li>
              <li>Be honest about mistakes and raise concerns through the appropriate route.</li>
              <li>Keep learning and accept constructive feedback.</li>
              <li>Maintain clear professional boundaries.</li>
            </ul>
            <p>
              These themes are reflected in the{" "}
              <a
                href="https://www.skillsforcare.org.uk/resources/documents/Support-for-leaders-and-managers/Managing-people/Code-of-conduct/Code-of-Conduct.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple underline"
                data-testid="quiz-code-of-conduct-link"
              >
                Code of Conduct for Healthcare Support Workers and Adult Social Care Workers in England
              </a>
              .
            </p>
            <p>
              If you recognise a gap, give it attention. That might mean training, supervision, clearer arrangements or
              changing a habit.
            </p>
            <p className="font-medium text-ink">A flattering quiz result cannot make those foundations optional.</p>

            <h2 className="font-display font-semibold text-xl md:text-2xl text-ink pt-4">Take the quiz: what kind of carer are you?</h2>
            <p>
              This is an informal reflection exercise, not a validated assessment of competence or suitability. Your
              choices suggest what you tend to prioritise; they cannot establish how safely or skilfully you work in
              practice.
            </p>
            <p>
              Choose one answer for each question: the response closest to what you would{" "}
              <strong className="text-ink">actually do first on a difficult working day</strong>. Several answers may
              sound reasonable. Choose your usual instinct, rather than the answer you think a good carer ought to give.
            </p>
          </div>

          <div className="mt-6 rounded-2xl border border-teal-200 bg-teal-50 p-4 flex items-start gap-3 text-sm text-ink" data-testid="quiz-privacy-note">
            <Info className="w-5 h-5 text-teal shrink-0 mt-0.5" strokeWidth={1.75} />
            <span>{PRIVACY_NOTE}</span>
          </div>

          <div className="mt-6">
            <a href="#quiz-questions" className="btn-purple" data-testid="quiz-start-btn-secondary">
              Start the quiz <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* ── The ten questions ─────────────────────────────────────── */}
      <section id="quiz-questions" className="scroll-mt-20 section py-12 md:py-16 bg-surface border-t border-slate-100">
        <div className="container-jcc max-w-3xl">
          <SectionHeading
            eyebrow="The quiz"
            accent="purple"
            title="Choose one answer for each question"
            description={'Select one answer for each question, then choose \u201cSee my result\u201d. You can change any answer before calculating.'}
          />

          <div className="mt-6 flex items-center justify-between sticky top-16 md:top-20 z-10 bg-surface/90 backdrop-blur-sm py-2 -mx-1 px-1 rounded-xl">
            <span className="pill bg-purple/10 text-purple" data-testid="quiz-answered-count">
              {answeredCount} of {QUESTIONS.length} answered
            </span>
          </div>

          <div className="mt-6 space-y-5">
            {QUESTIONS.map((q) => (
              <QuestionBlock
                key={q.id}
                question={q}
                selected={answers[q.id]}
                onSelect={handleSelect}
                isMissing={missingQuestionId === q.id}
              />
            ))}
          </div>

          <div className="mt-8">
            <button type="button" onClick={handleSeeResult} className="btn-purple" data-testid="quiz-see-result-btn">
              See my result <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </section>

      {/* ── Personal result ───────────────────────────────────────── */}
      {result && (
        <section className="section py-12 md:py-16 border-t border-slate-100">
          <div className="container-jcc max-w-3xl">
            <ResultSummary result={result} />
          </div>
        </section>
      )}

      {/* ── Jump links above the type descriptions ────────────────── */}
      <section id="carer-type-jump-links" className="scroll-mt-20 pt-10 border-t border-slate-100">
        <div className="container-jcc max-w-3xl">
          <div className="overline text-teal mb-3">Jump to a section</div>
          <nav className="flex flex-wrap gap-x-1 gap-y-2 text-sm" aria-label="Carer type sections" data-testid="quiz-type-jump-nav">
            {TYPE_ORDER.map((t, i) => (
              <span key={t} className="flex items-center">
                <a href={`#${TYPE_INFO[t].id}`} className="text-purple hover:underline font-medium px-2" data-testid={`quiz-jump-${TYPE_INFO[t].id}`}>
                  {TYPE_INFO[t].name}
                </a>
                <span className="text-slate-300">·</span>
              </span>
            ))}
            <a href="#reflection-notes" className="text-teal hover:underline font-medium px-2" data-testid="quiz-jump-reflection-notes">
              Reflection notes
            </a>
          </nav>
        </div>
      </section>

      {/* ── Full carer-type descriptions ──────────────────────────── */}
      {TYPE_ORDER.map((t) => (
        <TypeSection key={t} themeKey={t} isLeading={!!result && !result.allX && result.leadingThemes.includes(t)} />
      ))}

      {/* ── Reflection notes ──────────────────────────────────────── */}
      <ReflectionNotesSection result={result} />

      {/* ── Closing reflection + JCC invitation ───────────────────── */}
      <section className="section py-14 md:py-20 border-t border-slate-100">
        <div className="container-jcc max-w-3xl">
          <h2 className="font-display font-bold text-2xl md:text-3xl">The important part comes after the result</h2>
          <div className="mt-4 space-y-4 text-ink-muted leading-relaxed">
            <p>
              You may recognise yourself immediately. You may discover that your strongest quality also has a habit
              attached to it that needs attention.
            </p>
            <p>That is useful.</p>
            <p>
              Choose one strength to keep developing and one habit to work on. If one of your reflection notes
              highlighted a concern, give that priority. Make the change specific: a clearer handover, a question
              asked sooner, a boundary agreed before a placement begins.
            </p>
            <p>
              Then consider how the person receiving your support experiences your care. Their feedback matters more
              than a label we give ourselves.
            </p>
          </div>

          <div className="mt-10 rounded-3xl border border-purple/20 bg-purple/5 p-7 md:p-10">
            <h3 className="font-display font-bold text-xl md:text-2xl">A community worth belonging to</h3>
            <div className="mt-3 space-y-3 text-ink-muted leading-relaxed">
              <p>
                At Just Care Community, we want to champion carers who combine compassion with responsibility: people
                who take pride in their work and remain willing to learn.
              </p>
              <p>
                We want independent care to have clearer conversations, better preparation and arrangements that
                respect both the person receiving care and the person providing it.
              </p>
              <p>If that is the kind of care you want to help build, there is a place for you in that conversation.</p>
            </div>
            <p className="mt-5 font-semibold text-ink">
              Explore{" "}
              <Link to="/" className="text-purple underline" data-testid="quiz-explore-jcc-link">
                Just Care Community
              </Link>{" "}
              and help raise the standard of independent care.
            </p>
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-ink-muted italic" data-testid="quiz-optional-reflection">
            Optional reflection: Did your result sound like you? Which question made you think most, and what is one
            thing you will take into your next working day? You can reflect privately; you do not need to disclose
            individual answers.
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button type="button" onClick={handleCopyLink} className="btn-outline text-sm" data-testid="quiz-copy-link-btn">
              <Link2 className="w-4 h-4" strokeWidth={1.75} /> Copy quiz link
            </button>
            <span className="text-xs text-ink-soft">Shares the quiz only — never your answers or result.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
